import { IBaseDataObject } from "./BaseDataObject";
import { BaseDataStore } from "./IDataStore";
import { IUserInfo } from "./IUserInfo";
import { DataStoreQueryCondition, DataStoreQueryConditionType } from "./DataStoreQueryCondition";
import { DataStoreConstants } from "./DataStoreConstants";
import { DataStoreUtilities } from "./DataStoreUtilities";
import { DataUtilities } from "../../Common/DataUtilities";
import { AppGlueError } from "../../AppGlueError";
import type { AppGlueAny } from "../../Common/AppGlueAny";

export class TestXDataStore<T extends IBaseDataObject> extends BaseDataStore<T> {
    static clearFunctions: Function[] = [];

    // this is where the data is stored.
    collectionName: string;
    // since we create different mechanism per team, we do not need to consider the team here.
    items: { [id: string]: T } = {};
    private teamFilterRequired: boolean = true;

    constructor(collectionName: string) {
        super();

        TestXDataStore.clearFunctions.push(() => {
            this.items = {};
        });

        this.collectionName = collectionName;

        for (let i of DataStoreConstants.NON_TEAM_COLLECTIONS) {
            if (i === this.collectionName) this.teamFilterRequired = false;
        }
    }

    async get(user: IUserInfo, id: string): Promise<T> {
        let item = this.items[id];

        if (item && this.teamFilterRequired)
            if (Reflect.get(item, "teamName") !== user.teamName)
                throw new AppGlueError("Attempt to access data in another team");

        if (!item) throw new AppGlueError("no item found in collection: " + this.collectionName + " with id " + id);

        return item;
    }

    getAll(user: IUserInfo): Promise<T[]> {
        if (this.teamFilterRequired) {
            return this.query(user, []);
        } else {
            return new Promise<T[]>((resolve) => {
                resolve(Object.values(this.items));
            });
        }
    }

    getByIds(user: IUserInfo, ids: string[]): Promise<T[]> {
        let output: T[] = [];

        for (let id of ids) {
            let v = this.items[id];
            if (v) {
                if (this.teamFilterRequired && Reflect.get(v, "teamName") !== user.teamName) {
                    // item not for team, do not add
                } else {
                    output.push(v);
                }
            }
        }

        return new Promise<T[]>((resolve) => {
            resolve(output);
        });
    }

    async query(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<T[]> {
        let output: T[] = [];

        // turn into array if not.
        if (!Array.isArray(conditions)) conditions = [conditions];

        if (this.teamFilterRequired) {
            conditions.push(
                new DataStoreQueryCondition(DataStoreQueryConditionType.Equals, "teamName", user.teamName!),
            );
        }

        for (let v of Object.values(this.items)) {
            let match = true;
            for (let c of conditions) {
                // test condition
                if (!DataStoreQueryCondition.evaluate(c, v)) {
                    match = false;
                    break;
                }
            }

            if (match) output.push(v);
        }

        return output;
    }

    queryForCount(user: IUserInfo, conditions: DataStoreQueryCondition | DataStoreQueryCondition[]): Promise<number> {
        return new Promise<number>((resolve) => {
            this.query(user, conditions).then((r) => {
                resolve(r.length);
            });
        });
    }

    store(user: IUserInfo, item: T | T[]): Promise<AppGlueAny> {
        let items: T[] = [];

        if (!Array.isArray(item)) {
            items = [item];
        } else {
            items = item;
        }

        for (let toSave of items) {
            let i = toSave;

            if (typeof Reflect.get(i, "beforeSave") === "function") {
                // @ts-ignore
                i.beforeSave();
            }

            if (!i.id) i.id = DataUtilities.generateUniqueId();
            this.items[i.id!] = i;

            if (this.teamFilterRequired) {
                for (let i of items) Reflect.set(i, "teamName", user.teamName);
            }

            DataStoreUtilities.setupSaveHistoryFields(i, user);
        }

        return new Promise<AppGlueAny>((resolve) => {
            // todo: this needs fixing.

            for (let i of items) {
                if (typeof Reflect.get(i, "afterSave") === "function") {
                    // @ts-ignore
                    i.afterSave();
                }
            }

            resolve(items[0]);
        });
    }

    delete(user: IUserInfo, id: string): Promise<boolean> {
        Reflect.deleteProperty(this.items, id);

        return new Promise<boolean>((resolve) => {
            resolve(true);
        });
    }
}
